// UI strings for the free audio tools' bridge into the music-video generator
// (VocalRemover.tsx, StemSplitter.tsx, EightBit.tsx, Mastering.tsx) and for
// a composer whose saved track is waiting on sign-in (DirectorHome.tsx,
// landingV2/ComposerV2.tsx, AuthModal.tsx). Merged into the i18n `toolBridge`
// namespace by registerToolBridgeI18n.ts; a language missing here falls back
// to `en` (i18next fallbackLng). Its own namespace, like clipChangeScope.ts,
// so a lazily loaded language file cannot clobber it.
//
// Why: in the 30 days to Oct 11 2026 the free audio tools brought 5,682
// sign-ups and 3 buyers. The bridge copy only existed as English defaults,
// so the ~45% of vocal-remover users on a localized page read English. A
// signed-out visitor whose track waited on sign-in saw a sign-in window with
// no mention of the track (35% went on, vs 82% for a song upload on the
// generator), and on closing it a "Make" button stuck on "Uploading audio".
//
// The free preview is 20 seconds (see freePreviewClaims.test.ts). No em or
// en dashes in this copy.

const toolBridgeTranslations = {
  en: {
    bridgeTitle: "Next: turn this song into an AI music video",
    bridgeSubtitle:
      "Your track is already uploaded, so you don't need to add it again. Includes a free 20-second preview.",
    makeVideo: "Turn this song into a music video",
    makeVideoInstrumental: "Turn the instrumental into a music video",
    useInstrumental: "Use the instrumental instead",
    turnItIntoVideo: "Turn it into a music video",
    useAsSinger: "Use as the singer in a music video",
    trackLoaded: "Your track is loaded. Pick how to start below.",
    signInToMake: "Sign in to make your video",
    authSubtitle:
      "Your track is saved. Sign in for free to turn it into a music video.",
  },
  es: {
    bridgeTitle: "Siguiente: convierte esta canción en un videoclip con IA",
    bridgeSubtitle:
      "Tu pista ya está subida, así que no hace falta añadirla de nuevo. Incluye una vista previa gratis de 20 segundos.",
    makeVideo: "Convertir esta canción en videoclip",
    makeVideoInstrumental: "Convertir la instrumental en videoclip",
    useInstrumental: "Usar la instrumental",
    turnItIntoVideo: "Convertirla en videoclip",
    useAsSinger: "Usar como cantante en un videoclip",
    trackLoaded: "Tu pista está cargada. Elige abajo cómo empezar.",
    signInToMake: "Inicia sesión para crear tu videoclip",
    authSubtitle:
      "Tu pista está guardada. Inicia sesión gratis para convertirla en un videoclip.",
  },
  ja: {
    bridgeTitle: "次へ：この曲をAIミュージックビデオにしましょう",
    bridgeSubtitle:
      "曲はアップロード済みなので、もう一度追加する必要はありません。20秒の無料プレビュー付きです。",
    makeVideo: "この曲でミュージックビデオを作る",
    makeVideoInstrumental: "インストゥルメンタルでミュージックビデオを作る",
    useInstrumental: "代わりにインストゥルメンタルを使う",
    turnItIntoVideo: "ミュージックビデオにする",
    useAsSinger: "この人物を歌手にしてMVを作る",
    trackLoaded: "曲を読み込みました。下で始め方を選んでください。",
    signInToMake: "ログインして動画を作る",
    authSubtitle:
      "曲は保存されています。無料でログインして、ミュージックビデオにしましょう。",
  },
  pt: {
    bridgeTitle: "Próximo passo: transforme esta música em um videoclipe com IA",
    bridgeSubtitle:
      "Sua faixa já foi enviada, então não precisa adicioná-la de novo. Inclui uma prévia grátis de 20 segundos.",
    makeVideo: "Transformar esta música em videoclipe",
    makeVideoInstrumental: "Transformar o instrumental em videoclipe",
    useInstrumental: "Usar o instrumental",
    turnItIntoVideo: "Transformar em videoclipe",
    useAsSinger: "Usar como cantor no videoclipe",
    trackLoaded: "Sua faixa está carregada. Escolha abaixo como começar.",
    signInToMake: "Entre para criar seu videoclipe",
    authSubtitle:
      "Sua faixa está salva. Entre grátis para transformá-la em um videoclipe.",
  },
  fr: {
    bridgeTitle: "Étape suivante : transformez ce morceau en clip IA",
    bridgeSubtitle:
      "Votre piste est déjà importée, inutile de l'ajouter à nouveau. Un aperçu gratuit de 20 secondes est inclus.",
    makeVideo: "Transformer ce morceau en clip",
    makeVideoInstrumental: "Transformer l'instrumental en clip",
    useInstrumental: "Utiliser l'instrumental",
    turnItIntoVideo: "En faire un clip",
    useAsSinger: "En faire le chanteur d'un clip",
    trackLoaded: "Votre piste est chargée. Choisissez ci-dessous comment commencer.",
    signInToMake: "Connectez-vous pour créer votre clip",
    authSubtitle:
      "Votre piste est enregistrée. Connectez-vous gratuitement pour en faire un clip.",
  },
  zh: {
    bridgeTitle: "下一步：把这首歌变成 AI 音乐视频",
    bridgeSubtitle: "你的音轨已上传，无需再次添加。含 20 秒免费预览。",
    makeVideo: "用这首歌制作音乐视频",
    makeVideoInstrumental: "用伴奏制作音乐视频",
    useInstrumental: "改用伴奏",
    turnItIntoVideo: "做成音乐视频",
    useAsSinger: "用这张图当音乐视频的歌手",
    trackLoaded: "音轨已载入，请在下方选择开始方式。",
    signInToMake: "登录后制作视频",
    authSubtitle: "你的音轨已保存。免费登录，即可把它做成音乐视频。",
  },
  ko: {
    bridgeTitle: "다음 단계: 이 노래를 AI 뮤직비디오로 만들어 보세요",
    bridgeSubtitle:
      "트랙이 이미 업로드되어 다시 추가할 필요가 없어요. 20초 무료 미리보기가 포함돼요.",
    makeVideo: "이 노래로 뮤직비디오 만들기",
    makeVideoInstrumental: "반주로 뮤직비디오 만들기",
    useInstrumental: "대신 반주 사용하기",
    turnItIntoVideo: "뮤직비디오로 만들기",
    useAsSinger: "이 사진을 뮤직비디오 가수로 쓰기",
    trackLoaded: "트랙을 불러왔어요. 아래에서 시작 방법을 골라 주세요.",
    signInToMake: "로그인하고 영상 만들기",
    authSubtitle:
      "트랙이 저장되어 있어요. 무료로 로그인하고 뮤직비디오로 만들어 보세요.",
  },
  id: {
    bridgeTitle: "Berikutnya: ubah lagu ini menjadi video musik AI",
    bridgeSubtitle:
      "Trek Anda sudah diunggah, jadi tidak perlu ditambahkan lagi. Termasuk pratinjau gratis 20 detik.",
    makeVideo: "Ubah lagu ini menjadi video musik",
    makeVideoInstrumental: "Ubah instrumental menjadi video musik",
    useInstrumental: "Pakai instrumental saja",
    turnItIntoVideo: "Jadikan video musik",
    useAsSinger: "Jadikan penyanyi di video musik",
    trackLoaded: "Trek Anda sudah dimuat. Pilih cara memulai di bawah.",
    signInToMake: "Masuk untuk membuat video Anda",
    authSubtitle:
      "Trek Anda tersimpan. Masuk gratis untuk mengubahnya menjadi video musik.",
  },
  it: {
    bridgeTitle:
      "Prossimo passo: trasforma questa canzone in un video musicale con IA",
    bridgeSubtitle:
      "La tua traccia è già caricata, non serve aggiungerla di nuovo. Include un'anteprima gratuita di 20 secondi.",
    makeVideo: "Trasforma questa canzone in un video musicale",
    makeVideoInstrumental: "Trasforma la base strumentale in un video musicale",
    useInstrumental: "Usa invece la base strumentale",
    turnItIntoVideo: "Trasformala in un video musicale",
    useAsSinger: "Usa come cantante in un video musicale",
    trackLoaded: "La tua traccia è caricata. Scegli qui sotto come iniziare.",
    signInToMake: "Accedi per creare il tuo video",
    authSubtitle:
      "La tua traccia è salvata. Accedi gratis per trasformarla in un video musicale.",
  },
  nl: {
    bridgeTitle: "Volgende stap: maak van dit nummer een AI-muziekvideo",
    bridgeSubtitle:
      "Je track is al geüpload, dus je hoeft hem niet opnieuw toe te voegen. Inclusief een gratis preview van 20 seconden.",
    makeVideo: "Maak van dit nummer een muziekvideo",
    makeVideoInstrumental: "Maak van de instrumental een muziekvideo",
    useInstrumental: "Gebruik liever de instrumental",
    turnItIntoVideo: "Maak er een muziekvideo van",
    useAsSinger: "Gebruik als zanger in een muziekvideo",
    trackLoaded: "Je track is geladen. Kies hieronder hoe je begint.",
    signInToMake: "Log in om je video te maken",
    authSubtitle:
      "Je track is bewaard. Log gratis in om er een muziekvideo van te maken.",
  },
  pl: {
    bridgeTitle: "Dalej: zamień tę piosenkę w teledysk AI",
    bridgeSubtitle:
      "Twój utwór jest już przesłany, nie musisz dodawać go ponownie. Zawiera darmowy 20-sekundowy podgląd.",
    makeVideo: "Zamień tę piosenkę w teledysk",
    makeVideoInstrumental: "Zamień podkład instrumentalny w teledysk",
    useInstrumental: "Użyj podkładu instrumentalnego",
    turnItIntoVideo: "Zamień w teledysk",
    useAsSinger: "Użyj jako wokalisty w teledysku",
    trackLoaded: "Twój utwór jest wczytany. Wybierz poniżej, jak zacząć.",
    signInToMake: "Zaloguj się, aby stworzyć teledysk",
    authSubtitle:
      "Twój utwór jest zapisany. Zaloguj się za darmo, aby zamienić go w teledysk.",
  },
  tr: {
    bridgeTitle: "Sıradaki adım: bu şarkıyı yapay zeka müzik videosuna dönüştür",
    bridgeSubtitle:
      "Parçan zaten yüklendi, tekrar eklemene gerek yok. 20 saniyelik ücretsiz önizleme dahil.",
    makeVideo: "Bu şarkıyı müzik videosuna dönüştür",
    makeVideoInstrumental: "Enstrümantali müzik videosuna dönüştür",
    useInstrumental: "Bunun yerine enstrümantali kullan",
    turnItIntoVideo: "Müzik videosuna dönüştür",
    useAsSinger: "Müzik videosunda şarkıcı olarak kullan",
    trackLoaded: "Parçan yüklendi. Aşağıdan nasıl başlayacağını seç.",
    signInToMake: "Videonu yapmak için giriş yap",
    authSubtitle:
      "Parçan kaydedildi. Müzik videosuna dönüştürmek için ücretsiz giriş yap.",
  },
  vi: {
    bridgeTitle: "Tiếp theo: biến bài hát này thành video ca nhạc AI",
    bridgeSubtitle:
      "Bản nhạc của bạn đã được tải lên, không cần thêm lại. Có bản xem trước miễn phí 20 giây.",
    makeVideo: "Biến bài hát này thành video ca nhạc",
    makeVideoInstrumental: "Biến bản nhạc nền thành video ca nhạc",
    useInstrumental: "Dùng bản nhạc nền thay thế",
    turnItIntoVideo: "Biến thành video ca nhạc",
    useAsSinger: "Dùng làm ca sĩ trong video ca nhạc",
    trackLoaded: "Bản nhạc của bạn đã sẵn sàng. Chọn cách bắt đầu bên dưới.",
    signInToMake: "Đăng nhập để tạo video",
    authSubtitle:
      "Bản nhạc của bạn đã được lưu. Đăng nhập miễn phí để biến nó thành video ca nhạc.",
  },
  de: {
    bridgeTitle: "Als Nächstes: Mach aus diesem Song ein KI-Musikvideo",
    bridgeSubtitle:
      "Dein Track ist schon hochgeladen, du musst ihn nicht noch einmal hinzufügen. Inklusive kostenloser 20-Sekunden-Vorschau.",
    makeVideo: "Aus diesem Song ein Musikvideo machen",
    makeVideoInstrumental: "Aus dem Instrumental ein Musikvideo machen",
    useInstrumental: "Stattdessen das Instrumental verwenden",
    turnItIntoVideo: "Ein Musikvideo daraus machen",
    useAsSinger: "Als Sänger im Musikvideo verwenden",
    trackLoaded: "Dein Track ist geladen. Wähle unten, wie du anfangen willst.",
    signInToMake: "Anmelden, um dein Video zu erstellen",
    authSubtitle:
      "Dein Track ist gespeichert. Melde dich kostenlos an, um daraus ein Musikvideo zu machen.",
  },
};

export default toolBridgeTranslations;
